import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_DEPENSE_CATEGORIE_LABELS,
  AGENCE_DEPENSE_CATEGORIE_OPTIONS,
  AGENCE_DEPENSE_CATEGORIE_TONES,
  type AgenceDepense,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import DepenseForm from "./components/DepenseForm";

type SortKey = "recent" | "ancien" | "montant";

const sortOptions = [
  { value: "recent", label: "Plus récentes" },
  { value: "ancien", label: "Plus anciennes" },
  { value: "montant", label: "Montant décroissant" },
];

export default function AgenceDepensesPage() {
  const { depenses, statsDepenses, immeubles, getBien, getImmeuble, removeDepense } = useAgenceData();
  const { proprietaires } = useProprietaires();
  const { can } = useEmployes();
  const [searchParams] = useSearchParams();

  const immeubleParam = searchParams.get("immeuble") ?? "";
  const bienParam = searchParams.get("bien") ?? "";
  const proprietaireParam = searchParams.get("proprietaire") ?? "";

  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [proprietaireFilter, setProprietaireFilter] = useState(proprietaireParam);
  const [categorieFilter, setCategorieFilter] = useState("");
  const [immeubleFilter, setImmeubleFilter] = useState(immeubleParam);
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<AgenceDepense | null>(null);
  const [toDelete, setToDelete] = useState<AgenceDepense | null>(null);

  const hasDepenses = depenses.length > 0;

  const proprietaireOptions = useMemo(
    () => [
      { value: "", label: "Tous les propriétaires" },
      ...proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    ],
    [proprietaires]
  );

  const categorieOptions = useMemo(
    () => [{ value: "", label: "Toutes les catégories" }, ...AGENCE_DEPENSE_CATEGORIE_OPTIONS],
    []
  );

  const immeubleOptions = useMemo(
    () => [
      { value: "", label: "Tous les immeubles" },
      ...immeubles
        .filter((immeuble) => !proprietaireFilter || immeuble.proprietaireId === proprietaireFilter)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    ],
    [immeubles, proprietaireFilter]
  );

  const categoriePrincipale = useMemo(() => {
    if (depenses.length === 0) return "—";
    const totals = new Map<string, number>();
    depenses.forEach((depense) => {
      totals.set(depense.categorie, (totals.get(depense.categorie) ?? 0) + depense.montant);
    });
    let best = "";
    let bestValue = -1;
    totals.forEach((value, key) => {
      if (value > bestValue) {
        bestValue = value;
        best = key;
      }
    });
    return best ? AGENCE_DEPENSE_CATEGORIE_LABELS[best as keyof typeof AGENCE_DEPENSE_CATEGORIE_LABELS] : "—";
  }, [depenses]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = depenses.filter((depense) => {
      const matchesProprietaire = !proprietaireFilter || depense.proprietaireId === proprietaireFilter;
      const matchesCategorie = !categorieFilter || depense.categorie === categorieFilter;
      const matchesImmeuble = !immeubleFilter || depense.immeubleId === immeubleFilter;
      const matchesBien = !bienParam || depense.bienId === bienParam;

      const bien = depense.bienId ? getBien(depense.bienId) : undefined;
      const immeuble = depense.immeubleId ? getImmeuble(depense.immeubleId) : undefined;
      const matchesSearch =
        !term ||
        [
          depense.libelle,
          AGENCE_DEPENSE_CATEGORIE_LABELS[depense.categorie],
          depense.description,
          bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          immeuble?.nom ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return matchesProprietaire && matchesCategorie && matchesImmeuble && matchesBien && matchesSearch;
    });

    return result.sort((a, b) => {
      if (sort === "montant") return b.montant - a.montant;
      if (sort === "ancien") return new Date(a.date).getTime() - new Date(b.date).getTime();
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [depenses, search, proprietaireFilter, categorieFilter, immeubleFilter, bienParam, sort, getBien, getImmeuble]);

  const isFiltering =
    Boolean(search.trim()) ||
    Boolean(proprietaireFilter) ||
    Boolean(categorieFilter) ||
    Boolean(immeubleFilter) ||
    Boolean(bienParam);

  const filteredTotal = useMemo(() => filtered.reduce((sum, depense) => sum + depense.montant, 0), [filtered]);

  const resetFilters = () => {
    setSearch("");
    setProprietaireFilter("");
    setCategorieFilter("");
    setImmeubleFilter("");
  };

  const exportColumns: ExportColumn<AgenceDepense>[] = [
    { header: "Libellé", value: (depense) => depense.libelle },
    { header: "Catégorie", value: (depense) => AGENCE_DEPENSE_CATEGORIE_LABELS[depense.categorie] },
    { header: "Montant (FCFA)", value: (depense) => formatFcfa(depense.montant) },
    { header: "Date", value: (depense) => formatDate(depense.date) },
    {
      header: "Propriétaire",
      value: (depense) => {
        const owner = proprietaires.find((item) => item.id === depense.proprietaireId);
        return owner ? proprietaireNomComplet(owner) : "";
      },
    },
    { header: "Immeuble", value: (depense) => getImmeuble(depense.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (depense) => {
        const bien = depense.bienId ? getBien(depense.bienId) : undefined;
        return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    { header: "Description", value: (depense) => depense.description },
  ];

  const columns: DataTableColumn<AgenceDepense>[] = [
    {
      key: "libelle",
      header: "Libellé",
      className: "min-w-[180px]",
      render: (depense) => (
        <span className="font-label font-medium text-foreground-950">
          {depense.libelle || formatFcfa(depense.montant)}
        </span>
      ),
    },
    {
      key: "categorie",
      header: "Catégorie",
      render: (depense) => (
        <Badge tone={AGENCE_DEPENSE_CATEGORIE_TONES[depense.categorie]}>
          {AGENCE_DEPENSE_CATEGORIE_LABELS[depense.categorie]}
        </Badge>
      ),
    },
    {
      key: "montant",
      header: "Montant",
      align: "right",
      className: "min-w-[110px]",
      render: (depense) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(depense.montant)}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      className: "min-w-[120px]",
      render: (depense) => <span className="text-foreground-700">{formatDate(depense.date)}</span>,
    },
    {
      key: "rattachement",
      header: "Rattachement",
      className: "min-w-[180px]",
      render: (depense) => {
        const bien = depense.bienId ? getBien(depense.bienId) : undefined;
        const immeuble = depense.immeubleId ? getImmeuble(depense.immeubleId) : undefined;
        const rattachement = [bien ? AGENCE_BIEN_TYPE_LABELS[bien.type] : "", immeuble?.nom ?? ""]
          .filter(Boolean)
          .join(" · ");
        return <span className="text-foreground-700">{rattachement || "Aucun"}</span>;
      },
    },
    {
      key: "justificatif",
      header: "Justificatif",
      className: "min-w-[140px]",
      render: (depense) =>
        depense.justificatif ? (
          <span className="text-foreground-700">{depense.justificatif}</span>
        ) : (
          <span className="text-foreground-400">—</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[170px]",
      render: (depense) => {
        const immeuble = depense.immeubleId ? getImmeuble(depense.immeubleId) : undefined;
        return (
          <div className="flex items-center justify-end gap-2">
            {immeuble && (
              <Link
                to={`/agence/immeubles/${immeuble.id}`}
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                Immeuble
                <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
              </Link>
            )}
            <CardActions
              onEdit={can("depenses.modifier") ? () => setEditing(depense) : undefined}
              onDelete={can("depenses.supprimer") ? () => setToDelete(depense) : undefined}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Dépenses</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Suivez les charges des immeubles et biens confiés à l'agence : réparation, électricité, plomberie,
            nettoyage, sécurité, entretien et autres, avec leur justificatif.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <ExportMenu rows={filtered} columns={exportColumns} filename="agence-depenses" title="Dépenses" />
          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-add-line text-base" aria-hidden="true" />
            Ajouter une dépense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-money-cny-circle-line" label="Dépenses" value={String(statsDepenses.total)} tone="primary" />
        <StatCard
          icon="ri-wallet-3-line"
          label="Montant total"
          value={formatFcfa(statsDepenses.montantTotal)}
          tone="accent"
        />
        <StatCard icon="ri-calendar-check-line" label="Ce mois" value={formatFcfa(statsDepenses.ceMois)} tone="secondary" />
        <StatCard icon="ri-pie-chart-line" label="Catégorie principale" value={categoriePrincipale} tone="neutral" />
      </div>

      {hasDepenses && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-dep-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="agence-dep-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Description, catégorie, immeuble, bien..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-dep-proprietaire-filter" className="text-xs font-label font-medium text-foreground-700">
                Propriétaire
              </label>
              <Select
                id="agence-dep-proprietaire-filter"
                value={proprietaireFilter}
                onChange={(event) => {
                  setProprietaireFilter(event.target.value);
                  setImmeubleFilter("");
                }}
                options={proprietaireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agence-dep-categorie-filter" className="text-xs font-label font-medium text-foreground-700">
                Catégorie
              </label>
              <Select
                id="agence-dep-categorie-filter"
                value={categorieFilter}
                onChange={(event) => setCategorieFilter(event.target.value)}
                options={categorieOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="agence-dep-immeuble-filter" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="agence-dep-immeuble-filter"
                value={immeubleFilter}
                onChange={(event) => setImmeubleFilter(event.target.value)}
                options={immeubleOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} dépense{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""} ·{" "}
              {formatFcfa(filteredTotal)}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-44">
                <Select
                  aria-label="Trier par"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {!hasDepenses && (
        <EmptyState
          icon="ri-money-cny-circle-line"
          title="Aucune dépense enregistrée"
          description="Ajoutez votre première dépense pour suivre les charges des immeubles et biens gérés, par catégorie."
          action={
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Ajouter une dépense
            </button>
          }
        />
      )}

      {hasDepenses && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucune dépense ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(depense) => depense.id}
          minWidthClass="min-w-[1100px]"
          caption="Dépenses"
        />
      )}

      <DepenseForm
        open={formOpen || Boolean(editing)}
        initial={editing ?? undefined}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        defaultProprietaireId={proprietaireParam || undefined}
        defaultImmeubleId={immeubleParam || undefined}
        defaultBienId={bienParam || undefined}
      />

      <ConfirmDeleteModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete) removeDepense(toDelete.id);
          setToDelete(null);
        }}
        title="Supprimer la dépense"
        description={
          toDelete
            ? `Voulez-vous vraiment supprimer la dépense de ${formatFcfa(toDelete.montant)} ? Cette action est définitive.`
            : ""
        }
      />
    </div>
  );
}